import {
  CacheItem,
  useGenericValueRetrieval,
} from '@/hooks/invoice_reports/useBaseValueRetrieval';
import type { ValueTransformer } from '@/hooks/invoice_reports/useShowSearchValue';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { getPageData } from '@/utils/getPageData';
import { InfiniteData } from '@tanstack/react-query';
import {
  GetOfficeMembersResponse,
  getGetTargetApplicantsForInvoiceReportsQueryKey,
} from 'ap-openapi';
import { AxiosResponse } from 'axios';
import { useCallback, useMemo } from 'react';

/**
 * Hook that provides agent_applicant_office_member_ids custom transformer for useShowSearchValue.
 * Resolves agent applicant office member IDs to display names from the React Query cache.
 */

type TargetApplicantsData = InfiniteData<
  AxiosResponse<GetOfficeMembersResponse, unknown>,
  unknown
>;

const TARGET_APPLICANTS_QUERY_KEY = [
  getGetTargetApplicantsForInvoiceReportsQueryKey()[0],
];

const extractTargetApplicants = (data: TargetApplicantsData): CacheItem[] =>
  getPageData(data, 'office_members').map((item) => ({
    id: item.id,
    name: item.name,
  }));

export const useAgentApplicantTransformer = (
  agentApplicantOptions?: BaseOption[]
) => {
  const { getValues } = useGenericValueRetrieval<TargetApplicantsData>({
    queryKey: TARGET_APPLICANTS_QUERY_KEY,
    extract: extractTargetApplicants,
  });

  const agentApplicantTransformer: ValueTransformer = useCallback(
    (value) => {
      const ids = Array.isArray(value)
        ? value
        : typeof value === 'string'
        ? [value]
        : [];
      return getValues(ids, agentApplicantOptions);
    },
    [getValues, agentApplicantOptions]
  );

  return useMemo(
    () => ({
      agent_applicant_office_member_ids: agentApplicantTransformer,
    }),
    [agentApplicantTransformer]
  );
};
